import { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useStudioUser } from "../../hooks/useStudioUser";
import { useAuth } from "../../contexts/AuthContext";
import Notification from "../Notification";

function todayLocal(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function formatClosureDate(date: string): string {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Dates the studio is closed. Public trial-class booking skips these dates.
export default function StudioClosureDates() {
  const { studioUser } = useStudioUser();
  const { getAuthToken } = useAuth();
  const studioId = studioUser?.studio_id;

  const [closureDates, setClosureDates] = useState<string[]>([]);
  const [newDate, setNewDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({ show: false, title: "", message: "", type: "success" });

  useEffect(() => {
    const authToken = getAuthToken();
    if (!studioId || !authToken) return;
    setLoading(true);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioId}/closure-dates`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    )
      .then((response) => (response.ok ? response.json() : { closure_dates: [] }))
      .then((data) => setClosureDates(data.closure_dates || []))
      .catch(() => setClosureDates([]))
      .finally(() => setLoading(false));
  }, [studioId, getAuthToken]);

  const saveClosureDates = async (nextDates: string[]) => {
    const authToken = getAuthToken();
    if (!studioId || !authToken) return false;
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioId}/closure-dates`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ closure_dates: nextDates }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to save closure dates");
      setClosureDates(data.closure_dates || []);
      return true;
    } catch (error) {
      setNotification({
        show: true,
        title: "Could not save",
        message: error instanceof Error ? error.message : "Failed to save closure dates",
        type: "error",
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!newDate || closureDates.includes(newDate)) {
      setNewDate("");
      return;
    }
    if (await saveClosureDates([...closureDates, newDate])) {
      setNewDate("");
    }
  };

  const today = todayLocal();
  const upcomingDates = closureDates.filter((date) => date >= today);

  return (
    <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
      <Notification
        show={notification.show}
        title={notification.title}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
      />
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        Studio Closures
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Add the days your studio is closed. Leads booking a trial class from your
        website won&apos;t be offered classes on these dates.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          value={newDate}
          min={today}
          onChange={(event) => setNewDate(event.target.value)}
          disabled={loading || saving}
          aria-label="Closure date"
          className="rounded-md border-0 px-3 py-2 text-sm text-gray-900 bg-white shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:bg-gray-700 dark:text-white dark:ring-gray-600"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!newDate || loading || saving}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Add Closure"}
        </button>
      </div>

      <div className="mt-4">
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
        ) : upcomingDates.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No upcoming closures.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {upcomingDates.map((date) => (
              <li
                key={date}
                className="inline-flex items-center gap-1 rounded-full bg-gray-100 py-1 pl-3 pr-1 text-sm text-gray-800 dark:bg-gray-700 dark:text-gray-100"
              >
                {formatClosureDate(date)}
                <button
                  type="button"
                  onClick={() =>
                    saveClosureDates(closureDates.filter((value) => value !== date))
                  }
                  disabled={saving}
                  aria-label={`Remove ${formatClosureDate(date)}`}
                  className="rounded-full p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-50 dark:hover:bg-gray-600 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
